/**
 * ThumbnailGenerator - Captures canvas screenshots for sequence thumbnails
 */

import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Logger } from './Logger';

const logger = Logger.getLogger('ThumbnailGenerator');

export interface ThumbnailOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'image/jpeg' | 'image/png' | 'image/webp';
  addBranding?: boolean; // Add XRGallery branding overlay
  tourName?: string; // Tour name for headline
}

const DEFAULT_OPTIONS: Required<Omit<ThumbnailOptions, 'tourName'>> & { tourName?: string } = {
  width: 1200,
  height: 630, // Open Graph recommended size
  quality: 0.85,
  format: 'image/jpeg',
  addBranding: true,
  tourName: undefined
};

/**
 * Capture a thumbnail from a canvas element
 * @param canvas The canvas element to capture from
 * @param options Optional configuration for thumbnail
 * @returns Data URL of the captured thumbnail
 */
export async function captureCanvasThumbnail(
  canvas: HTMLCanvasElement,
  options: ThumbnailOptions = {}
): Promise<string> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Create canvas at target dimensions
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = opts.width;
  resizedCanvas.height = opts.height;
  const ctx = resizedCanvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get 2D context for thumbnail');
  }

  // Draw original canvas scaled to new dimensions
  ctx.drawImage(canvas, 0, 0, opts.width, opts.height);

  // Add branding overlay if enabled
  if (opts.addBranding) {
    addBrandingOverlay(ctx, opts.width, opts.height, opts.tourName);
  }

  return resizedCanvas.toDataURL(opts.format, opts.quality);
}

/**
 * Add branding overlay to the thumbnail
 * Includes XRGallery logo/text and a call-to-action
 */
function addBrandingOverlay(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  tourName?: string
): void {
  // Add subtle gradient overlay at bottom for text readability
  const gradient = ctx.createLinearGradient(0, height * 0.65, 0, height);
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
  gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.3)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0.6)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, height * 0.65, width, height * 0.35);

  // XRGallery branding (bottom left)
  ctx.font = 'bold 24px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.textBaseline = 'bottom';
  ctx.fillText('XRGallery', 30, height - 25);

  // Tour name headline (bottom center-left, if provided)
  if (tourName) {
    ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = 'white';
    ctx.textBaseline = 'bottom';

    // Truncate if too long
    const maxWidth = width - 350;
    let displayName = tourName;
    while (ctx.measureText(displayName).width > maxWidth && displayName.length > 10) {
      displayName = displayName.slice(0, -4) + '...';
    }
    ctx.fillText(displayName, 30, height - 60);
  }

  // Call-to-action button (bottom right)
  const ctaText = 'View Tour →';
  ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  const ctaWidth = ctx.measureText(ctaText).width + 40;
  const ctaHeight = 44;
  const ctaX = width - ctaWidth - 30;
  const ctaY = height - ctaHeight - 20;

  // CTA button background
  ctx.fillStyle = 'rgba(255, 107, 107, 0.95)'; // XRGallery primary color
  ctx.beginPath();
  ctx.roundRect(ctaX, ctaY, ctaWidth, ctaHeight, 8);
  ctx.fill();

  // CTA text
  ctx.fillStyle = 'white';
  ctx.textBaseline = 'middle';
  ctx.fillText(ctaText, ctaX + 20, ctaY + ctaHeight / 2);
}

/**
 * Convert data URL to Blob
 * @param dataUrl Data URL from canvas.toDataURL()
 * @returns Blob object
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(parts[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);

  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }

  return new Blob([u8arr], { type: mime });
}

/**
 * Upload thumbnail to Firebase Storage
 * @param sequenceId The sequence ID
 * @param dataUrl Data URL of the thumbnail
 * @returns Public URL of the uploaded thumbnail
 */
export async function uploadThumbnail(
  sequenceId: string,
  dataUrl: string
): Promise<string> {
  try {
    const blob = dataUrlToBlob(dataUrl);
    const thumbnailRef = ref(storage, `thumbnails/${sequenceId}/preview.jpg`);

    // Upload with metadata
    const metadata = {
      contentType: 'image/jpeg',
      cacheControl: 'public, max-age=31536000', // Cache for 1 year
    };

    await uploadBytes(thumbnailRef, blob, metadata);

    // Get public download URL
    const downloadUrl = await getDownloadURL(thumbnailRef);

    logger.info('Thumbnail uploaded successfully:', downloadUrl);
    return downloadUrl;
  } catch (error) {
    logger.error('Error uploading thumbnail:', error);
    throw new Error('Failed to upload thumbnail');
  }
}

/**
 * Generate and upload thumbnail from canvas in one step
 * @param canvas The canvas element to capture from
 * @param sequenceId The sequence ID for storage path
 * @param options Optional thumbnail configuration
 * @returns Public URL of the uploaded thumbnail
 */
export async function generateAndUploadThumbnail(
  canvas: HTMLCanvasElement,
  sequenceId: string,
  options: ThumbnailOptions = {}
): Promise<string> {
  const dataUrl = await captureCanvasThumbnail(canvas, options);
  return await uploadThumbnail(sequenceId, dataUrl);
}

/**
 * Upload a custom thumbnail image file
 * @param sequenceId The sequence ID for storage path
 * @param file The image file to upload
 * @returns Public URL of the uploaded thumbnail
 */
export async function uploadCustomThumbnail(
  sequenceId: string,
  file: File
): Promise<string> {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Resize image to OG dimensions
    const resizedBlob = await resizeImageFile(file, 1200, 630);

    const thumbnailRef = ref(storage, `thumbnails/${sequenceId}/preview.jpg`);

    const metadata = {
      contentType: 'image/jpeg',
      cacheControl: 'public, max-age=31536000',
    };

    await uploadBytes(thumbnailRef, resizedBlob, metadata);

    const downloadUrl = await getDownloadURL(thumbnailRef);

    logger.info('Custom thumbnail uploaded successfully:', downloadUrl);
    return downloadUrl;
  } catch (error) {
    logger.error('Error uploading custom thumbnail:', error);
    throw new Error('Failed to upload custom thumbnail');
  }
}

/**
 * Resize an image file to target dimensions
 */
async function resizeImageFile(
  file: File,
  targetWidth: number,
  targetHeight: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get 2D context'));
        return;
      }

      // Draw image scaled to fit (center crop if aspect ratio differs)
      const srcAspect = img.width / img.height;
      const dstAspect = targetWidth / targetHeight;

      let sx = 0, sy = 0, sw = img.width, sh = img.height;

      if (srcAspect > dstAspect) {
        // Source is wider - crop sides
        sw = img.height * dstAspect;
        sx = (img.width - sw) / 2;
      } else {
        // Source is taller - crop top/bottom
        sh = img.width / dstAspect;
        sy = (img.height - sh) / 2;
      }

      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/jpeg',
        0.9
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Wait for the scene to settle before capturing thumbnail
 * Ensures all assets are loaded and rendered
 * @param frames Number of frames to wait (default: 3)
 */
export function waitForSceneSettle(frames: number = 3): Promise<void> {
  return new Promise((resolve) => {
    let frameCount = 0;
    const checkFrame = () => {
      frameCount++;
      if (frameCount >= frames) {
        resolve();
      } else {
        requestAnimationFrame(checkFrame);
      }
    };
    requestAnimationFrame(checkFrame);
  });
}
